import { useMemo } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { ascend, compose, descend, find, includes, prop, propOr, sortWith } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { getLabelMessage } from '@gef-ui/localization/messages';
import BadgeDollarSolid from '@gef-ui/material-icons/BadgeDollarSolid';
import TriangleExclamationSolid from '@gef-ui/material-icons/TriangleExclamationSolid';
import UserRegular from '@gef-ui/material-icons/UserRegular';

import { PKUrl } from 'components';
import { Gcp, GcpBox, GcpOutline, Gp, GpBox, GpOutline, MoreTime } from 'components/atoms/CustomSVGIcon';
import { codelists } from 'constants/codelists';
import { KCL_DISPLAY_SEMAPHORE } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { getLastRequest } from 'features/common/fetch/selectors';
import { PPZ_SUBORDINATE_ROLE_CODE } from 'features/views/policyOverview/constants';
import { contactAgeIdLocalizations, contactAgeIds } from 'features/views/semaphore/constants';
import { chooseColorBasedOnId } from 'features/views/semaphore/utils';
import { getHasPPZSubordinatesOrIsPPZSubordinate } from 'features/views/user/selectors';
import { currency, equalOrThreeDashes, fromIsoDate, getCodelistByName, getCodelistPropById } from 'utils';
import { getClientName } from 'utils/client';
import { equalOrValue } from 'utils/equalOrThreeDashes';
import { joinString } from 'utils/string';

const { oneToTwoYears, threeToFourYears, fiveToSixYears, sevenAndLaterYears, semaphorePO } = contactAgeIds;

// used for dynamic generation of companySemaphoreIcon
const iconComponents = {
	gcpBox: GcpBox,
	gpBox: GpBox,
	gpOutline: GpOutline,
	gcpOutline: GcpOutline,
};

export const ResultsTableRow = (props) => {
	const { policy = {}, hasPermissionISP } = props;

	const {
		carRegNo,
		instanceCompanyId,
		firstPaymentState,
		policyHolder = {},
		policyOpenDate,
		productLines,
		policyAdmins,
		policyPremium,
		policyStatusId,
		policyValueDate,
		policyFinalDate,
		policyFrameworkNo,
		policyNo,
		systemId,
		lastCommunicationAgeId,
	} = policy;

	const { address } = policyHolder;
	const clientName = getClientName(policyHolder);
	const sellerLogin = compose(
		propOr(null, 'login'),
		find((item) => item.roleCode === PPZ_SUBORDINATE_ROLE_CODE),
	)(policyAdmins || []);

	const policyStatusCodelist = useSelector(getCodelistByName(codelists.policyStatuses));
	const productLinesCodelist = useSelector(getCodelistByName(codelists.products));
	const paymentStateCodelist = useSelector(getCodelistByName(codelists.policyPaymentStatuses));

	const showSemaphore = useSelector((state) => hasPermissionByIds(state)(KCL_DISPLAY_SEMAPHORE));
	const hasPPZSubordinatesOrIsPPZSubordinate = useSelector(getHasPPZSubordinatesOrIsPPZSubordinate);
	const { body: lastRequestBody } = useSelector(getLastRequest(fetch.constants.urls.policyList));

	const productLinesFullName = useMemo(() => {
		return productLinesCodelist.map((item) => ({
			...item,
			fullName: joinString(' - ', [item?.shortDescription, item?.jokName]),
		}));
	}, [productLinesCodelist]);

	const generateCompanyIcon = (companyId) => (
		<IconButton disableRipple sx={{ color: 'primary.main' }}>
			{companyId === 1 ? <Gcp width={22} height={22} /> : <Gp width={25} height={25} />}
		</IconButton>
	);

	const generateCompanySemaphoreIcon = (companyId, semaphoreId) => {
		const icon = {};

		if (isNilOrEmptyString(semaphoreId)) {
			icon.component = MoreTime;
			icon.color = 'grey.600';
		} else {
			const company = companyId === 1 ? 'gcp' : 'gp';

			let iconType;

			if (includes(semaphoreId, [oneToTwoYears, threeToFourYears, fiveToSixYears, sevenAndLaterYears])) {
				iconType = 'Box';
			}

			if (semaphoreId === semaphorePO) {
				iconType = 'Outline';
			}

			const resultIcon = company.concat(iconType);
			const ChosenCompanySemaphoreIcon = iconComponents[resultIcon];
			const color = chooseColorBasedOnId(semaphoreId);

			icon.component = ChosenCompanySemaphoreIcon;
			icon.color = color;
		}

		const { color, component: Icon } = icon;

		const title = isNilOrEmptyString(semaphoreId)
			? getLabelMessage('semaphore:semaphoreCalculationInProcess')
			: getLabelMessage(contactAgeIdLocalizations[semaphoreId]);

		return (
			<Tooltip title={title}>
				<IconButton disableRipple sx={{ color }}>
					<Icon />
				</IconButton>
			</Tooltip>
		);
	};

	const renderIcon = (companyId, semaphoreId, hasSemaphorePermission) => {
		const correctIds = Object.values(contactAgeIds);
		const belongsToCorrectIdsToShow = includes(semaphoreId, correctIds) || isNilOrEmptyString(semaphoreId);

		if (hasSemaphorePermission && belongsToCorrectIdsToShow) {
			return generateCompanySemaphoreIcon(companyId, semaphoreId);
		} else {
			return generateCompanyIcon(companyId);
		}
	};

	const generateTrimmedProductLineWithPossibleTooltip = ({ productLine, productLinesFullName }) => {
		const chosenProductLineName = equalOrThreeDashes(
			getCodelistPropById(productLine, productLinesFullName, 'fullName'),
		);

		let result;
		if (chosenProductLineName.length > 50) {
			const shortenedName = chosenProductLineName.slice(0, 50).concat('...');
			result = (
				<Tooltip title={chosenProductLineName}>
					<div>{shortenedName}</div>
				</Tooltip>
			);
		} else {
			result = chosenProductLineName;
		}

		return <div key={productLine}>{result}</div>;
	};

	const withSubordinatesTooltip = useMemo(() => {
		if (isNilOrEmpty(policyAdmins)) {
			return getLabelMessage('policyOverview:noPolicyAdminAvailable');
		} else if (policyAdmins?.length === 1) {
			return equalOrValue(getLabelMessage('policyOverview:inactivePolicyAdmin'), policyAdmins[0]?.adminFullName);
		} else {
			const sortedPolicyAdmins = sortWith([ascend(prop('orderNr')), descend(propOr(0, 'sellerIn'))])(
				policyAdmins,
			);

			return (
				<List sx={{ listStyleType: 'disc' }}>
					{sortedPolicyAdmins.map((admin) => {
						const isSubSeller = isNilOrEmpty(admin?.sellerIn);

						return (
							<ListItem sx={{ px: 0.5, py: 0, ...(isSubSeller && { ml: 4, display: 'list-item' }) }}>
								{!isSubSeller
									? joinString('. ', [
											admin?.orderNr,
											equalOrValue(
												getLabelMessage('policyOverview:inactivePolicyAdmin'),
												admin?.adminFullName,
											),
									  ])
									: equalOrValue(
											getLabelMessage('policyOverview:inactivePolicyAdmin'),
											admin?.adminFullName,
									  )}
							</ListItem>
						);
					})}
				</List>
			);
		}
	}, [policyAdmins]);

	const firstPaymentValue = useMemo(
		() => (
			<Stack
				direction="row"
				spacing={0.5}
				sx={{
					alignItems: 'center',
				}}
			>
				{equalOrThreeDashes(getCodelistPropById(firstPaymentState, paymentStateCodelist, 'shortDescription'))}
				{systemId === 5 && (
					<Tooltip title={getLabelMessage('policyOverview:firstPaymentStateSiraelTypeTooltip')}>
						<IconButton disableRipple sx={{ color: 'warning.main' }}>
							<TriangleExclamationSolid />
						</IconButton>
					</Tooltip>
				)}
			</Stack>
		),
		[firstPaymentState, paymentStateCodelist, systemId],
	);

	return (
		<>
			<TableCell>{renderIcon(instanceCompanyId, lastCommunicationAgeId, showSemaphore)}</TableCell>
			<TableCell>
				<PKUrl.PKPolicy
					partnerId={policyHolder.id}
					policyNo={policyNo}
					systemId={systemId}
					targetPage={[1, 3, 4, 5].includes(parseInt(policyStatusId)) ? 'GPK-113' : 'GPK-106'}
					muiLink
				>
					{equalOrThreeDashes(policyNo)}/{equalOrThreeDashes(policyFrameworkNo)}
				</PKUrl.PKPolicy>
				<PKUrl.PKPartner partnerId={policyHolder.id} muiLink>
					{equalOrThreeDashes(clientName)}
				</PKUrl.PKPartner>
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(carRegNo)}</div>
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(address?.municipality)}</div>
				<div>{equalOrThreeDashes(address?.zipCode)}</div>
			</TableCell>
			<TableCell>
				{!isNilOrEmpty(productLines)
					? productLines.map((productLine) =>
							generateTrimmedProductLineWithPossibleTooltip({ productLine, productLinesFullName }),
					  )
					: equalOrThreeDashes()}
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(currency(policyPremium))}</div>
				<div>{equalOrThreeDashes(getCodelistPropById(policyStatusId, policyStatusCodelist))}</div>
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(fromIsoDate(policyOpenDate))}</div>
				{firstPaymentValue}
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(fromIsoDate(policyValueDate))}</div>
				<div>{equalOrThreeDashes(fromIsoDate(policyFinalDate))}</div>
			</TableCell>
			{hasPPZSubordinatesOrIsPPZSubordinate && <TableCell>{equalOrThreeDashes(sellerLogin)}</TableCell>}
			{lastRequestBody?.searchAcrossSubordinates && (
				<TableCell>
					<Tooltip title={withSubordinatesTooltip}>
						<IconButton disableRipple sx={{ color: 'primary.main' }}>
							<UserRegular />
						</IconButton>
					</Tooltip>
				</TableCell>
			)}
			{hasPermissionISP && (
				<TableCell>
					<PKUrl.ISPPolicy policyNo={policyNo} systemId={systemId}>
						<Tooltip title={getLabelMessage('policyOverview:provision')}>
							<IconButton disableRipple sx={{ color: 'primary.main' }}>
								<BadgeDollarSolid />
							</IconButton>
						</Tooltip>
					</PKUrl.ISPPolicy>
				</TableCell>
			)}
		</>
	);
};

ResultsTableRow.propTypes = {
	hasPermissionISP: PropTypes.bool,
	policy: PropTypes.object,
};

export default ResultsTableRow;
